<template>
  <layout-basic>
    <section class="faq-page">
      <div class="faq-hero">
        <h1 class="font-sora">FAQs</h1>
        <p>Frequently asked questions about our microlearning platform.</p>
      </div>

      <div class="faq-list-wrap">
        <div class="faq-list">
          <div
            v-for="(item, index) in faqItems"
            :key="index"
            class="faq-item"
            :class="{ open: expandedPanel === index }"
          >
            <button
              type="button"
              class="faq-question"
              :aria-expanded="expandedPanel === index ? 'true' : 'false'"
              @click="toggle(index)"
            >
              <span>{{ item.question }}</span>
              <i :class="expandedPanel === index ? 'fas fa-minus' : 'fas fa-plus'" aria-hidden="true"></i>
            </button>
            <div v-show="expandedPanel === index" class="faq-answer">
              <p v-html="item.answer"></p>
            </div>
          </div>
        </div>
      </div>

      <section class="faq-cta-band">
        <img
          class="faq-cta-band__cat"
          :src="getImageSource('training-courses-for-smes-with-improve.png', 'homepage/demo')"
          alt=""
        />
        <div class="faq-cta-band__inner">
          <h2 class="font-sora">Watch the Demo</h2>
          <p>See IMPROVE in action and discover how it helps growing businesses.</p>
          <a href="/watch-demo" class="faq-cta-band__btn">Let's Go</a>
        </div>
      </section>

      <standard-cta
        title="Still have questions?"
        lead="Watch the demo, experience IMPROVE yourself, or get started when you’re ready."
      />
    </section>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import StandardCta from '@/components/marketing/StandardCta.vue';
import { screensize } from '../../utils/screensize';

const getCurrentScreenSize = screensize();

export default {
  name: 'FAQ',
  components: {
    LayoutBasic,
    StandardCta,
  },
  data() {
    return {
      expandedPanel: 0,
      screenSize: getCurrentScreenSize(),
      faqItems: [
        {
          question: 'How do I contact IMPROVE?',
          answer: 'Reach out anytime to <b>help@improve.online</b>.',
        },
        {
          question: 'How long is the subscription?',
          answer:
            'We offer annual subscriptions or you can lock in the price with a 3-year subscription.',
        },
        {
          question: 'Can I pay monthly?',
          answer:
            'No, the full cost of the subscription must be paid upfront before your account can be set to live.',
        },
        {
          question: 'What is an LMS?',
          answer:
            "An LMS is a learning management system — a place to keep and organise all training information in an organisation. It allows people to take courses, track access, and report on use.",
        },
        {
          question: 'How do the HR integrations work?',
          answer:
            "Our current HR integrations are listed on our website. At a basic level, when you add a user to your HR system it will also add that user to IMPROVE. See all HR integrations <b><a href='/hr-integrations'>here</a></b>.",
        },
        {
          question: 'Can you integrate with any HR system?',
          answer:
            "If the HR system you use is not listed, get in touch and we can see if it's possible to integrate. See all HR integrations <b><a href='/hr-integrations'>here</a></b>.",
        },
        {
          question: 'How does the pricing work?',
          answer:
            "Pricing is transparent and based on the size of your business. See our current plans on the <b><a href='/pricing'>pricing page</a></b>. For 250+ users, contact us for a bespoke quote.",
        },
        {
          question: 'What is the maximum users I can have?',
          answer:
            'The system allows up to the maximum for your chosen plan bracket. Larger organisations can contact us for a custom arrangement.',
        },
        {
          question: 'Can I add more users?',
          answer:
            'At any point, you can prorate your subscription to add more users and move to the next bracket.',
        },
        {
          question: 'How do I pay?',
          answer:
            'We issue an invoice and you can pay electronically via BACS or by card. You can also <b><a href="/get-started">Get Started</a></b> online with secure card payment.',
        },
        {
          question: 'How long is the free trial?',
          answer:
            "Two weeks. Talk to us if you would like to extend or show other team members. You can also <b><a href='/watch-demo'>watch the demo</a></b> or Experience IMPROVE via the trial signup.",
        },
        {
          question: 'How do I get a live account after a free trial?',
          answer:
            "Email <b>help@improve.online</b> or <b><a href='/get-started'>Get Started</a></b> online. Once payment is complete, we set your account status to live.",
        },
        {
          question: 'Is there a reporting feature?',
          answer:
            'Yes, you can run reports within the system and download reports in PDF or XLS format.',
        },
        {
          question: 'Can I give access to different teams?',
          answer:
            "We have a team manager function which lets you allocate users to team leaders so they can assign courses and monitor progress.",
        },
        {
          question: 'Can I partner with IMPROVE or resell IMPROVE?',
          answer:
            "Yes — get in touch to discuss partnership options, or visit our <b><a href='/partner-programme'>Partners</a></b> page.",
        },
        {
          question: 'What is a Playlist?',
          answer:
            "A playlist categorises course content. For example, an induction playlist can include courses for new employees plus extra content such as a building map or contact details.",
        },
        {
          question: 'User count, is this active users only?',
          answer:
            'Users you no longer need can be archived so they do not count in your user bracket.',
        },
        {
          question: 'Does it work on a Mobile device?',
          answer:
            'Yes, IMPROVE is fully responsive and adjusts to the size of the screen or device you are using.',
        },
        {
          question: 'How often can I access IMPROVE?',
          answer: '24/7 and as many times as you need.',
        },
        {
          question: 'How many courses can each user take?',
          answer: 'As many as they need, whenever they need them.',
        },
        {
          question: 'Will I get a certificate?',
          answer:
            'Yes — every course provides a personalised certificate on successful completion.',
        },
        {
          question: 'Will I get any training on the platform?',
          answer:
            'We onboard company admins via Teams through the IMPROVE Launch Programme.',
        },
        {
          question: 'Is it easy to onboard staff?',
          answer: 'Yes, and we are here to help.',
        },
        {
          question: 'How many admins can I have?',
          answer: 'You can have up to 10 admins on the platform.',
        },
        {
          question: 'How do I cancel my subscription?',
          answer:
            'Contact <b>help@improve.online</b>. We will help you cancel automatic renewal at least 30 days before it is due to renew.',
        },
        {
          question: 'How do I contact you about an issue?',
          answer:
            'Email <b>support@improve.online</b> and one of our team will help.',
        },
      ],
    };
  },
  metaInfo() {
    return {
      title: 'IMPROVE: FAQs about our microlearning platform for businesses.',
      meta: [
        {
          name: 'description',
          content:
            'Our FAQs address plans and payment, training courses, trials, and becoming an IMPROVE partner.',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://www.improve.online/faq' }],
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    toggle(index) {
      this.expandedPanel = this.expandedPanel === index ? null : index;
    },
    handleResize() {
      this.screenSize = getCurrentScreenSize();
    },
    getImageSource(imageName, filepath) {
      return `/static/${filepath}/${this.screenSize}/${imageName}`;
    },
  },
};
</script>

<style scoped>
.faq-page {
  background: #ffffff;
}

.faq-hero {
  background-image: url(/static/single-page/banner.png);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  text-align: center;
  padding: 72px 20px 48px;
}

.faq-hero h1 {
  margin: 0 0 12px;
  color: #002458;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.15;
}

.faq-hero p {
  margin: 0 auto;
  max-width: 560px;
  color: #6b7280;
  font-size: 17px;
  line-height: 1.5;
}

.faq-list-wrap {
  width: 100%;
  background: #ffffff;
  padding: 24px 16px 64px;
}

.faq-list {
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
}

.faq-item {
  border-bottom: 1px solid #d9d9d9;
}

.faq-question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 4px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  color: #08a0a8;
  line-height: 1.35;
}

.faq-question span {
  flex: 1 1 auto;
  min-width: 0;
}

.faq-question i {
  flex: 0 0 auto;
  color: #002458;
  font-size: 14px;
}

.faq-question:hover {
  color: #078f96;
}

.faq-answer {
  padding: 0 4px 18px;
}

.faq-answer p {
  margin: 0;
  color: #334155;
  font-size: 16px;
  line-height: 1.55;
}

.faq-answer >>> a,
.faq-answer /deep/ a {
  color: #08a0a8;
  font-weight: 600;
}

/* Self-contained green CTA — does not use fragile global demo CSS */
.faq-cta-band {
  position: relative;
  background: #66c791;
  text-align: center;
  padding: 72px 20px 56px;
  margin-top: 72px;
  overflow: visible;
}

.faq-cta-band__cat {
  position: absolute;
  left: 50%;
  bottom: calc(100% - 8px);
  transform: translateX(-50%);
  width: min(220px, 45vw);
  height: auto;
  pointer-events: none;
  z-index: 2;
}

.faq-cta-band__inner {
  position: relative;
  z-index: 3;
  max-width: 640px;
  margin: 0 auto;
}

.faq-cta-band h2 {
  margin: 0 0 12px;
  color: #ffffff;
  font-size: 40px;
  font-weight: 700;
}

.faq-cta-band p {
  margin: 0 0 24px;
  color: #e7f8ff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.45;
}

.faq-cta-band__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  height: 48px;
  padding: 0 28px;
  background: #002458;
  color: #ffffff !important;
  font-size: 17px;
  font-weight: 700;
  text-decoration: none;
  border-radius: 4px;
}

.faq-cta-band__btn:hover {
  background: #08a0a8;
  color: #ffffff !important;
}

@media screen and (max-width: 600px) {
  .faq-hero {
    padding: 48px 16px 32px;
  }

  .faq-hero h1 {
    font-size: 36px;
  }

  .faq-question {
    font-size: 16px;
  }

  .faq-cta-band {
    margin-top: 56px;
    padding: 56px 16px 40px;
  }

  .faq-cta-band h2 {
    font-size: 28px;
  }

  .faq-cta-band p {
    font-size: 15px;
  }
}
</style>
